/**
 * PlatformIntelligence — Phase 3 Frontend
 * 
 * Visual lineage graph (D3-compatible), trend arrows, notification center,
 * AI conflict detection, unified search. All data from /api/v1/platform/* APIs.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch } from '../services/api_service';


const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30", cardHover: "#162040",
  border: "#1a2744", borderHi: "#2a3a5c", text: "#e2e8f0", muted: "#6b7a96", dim: "#384660",
  blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981", purple: "#8b5cf6",
  cyan: "#06b6d4", red: "#ef4444", pink: "#ec4899", teal: "#14b8a6", orange: "#f97316",
};

const NODE_COLORS = {
  evidence: C.emerald, claim: C.blue, obligation: C.amber, datapoint: C.purple,
  framework: C.cyan, requirement: C.pink, supplier: C.teal, engagement: C.orange,
};

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api/v1/platform";

async function piFetch(path, options) {
  try {
    const headers = { "Content-Type": "application/json" };
    const token = localStorage.getItem("token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}${path}`, { headers, ...options });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

// ═══════════════════════════════════════════════════════════════
// FORCE GRAPH (SVG-based D3-compatible)
// ═══════════════════════════════════════════════════════════════

function ForceGraph({ nodes, links, selected, onSelect }) {
  const svgRef = useRef(null);
  const [positions, setPositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const W = 700, H = 480;

  useEffect(() => {
    if (!nodes.length || Object.keys(positions).length > 0) return;
    const pos = {};
    const typeGroups = {};
    nodes.forEach(n => { (typeGroups[n.type] = typeGroups[n.type] || []).push(n.id); });
    const typeOrder = ["evidence", "claim", "datapoint", "obligation", "requirement", "framework", "supplier", "engagement"];
    let col = 0;
    typeOrder.forEach(t => {
      const group = typeGroups[t] || [];
      group.forEach((id, i) => {
        pos[id] = {
          x: Math.min(W - 30, Math.max(30, 80 + col * 110 + (Math.random() - 0.5) * 30)),
          y: Math.min(H - 30, Math.max(30, 60 + i * 70 + (Math.random() - 0.5) * 20))
        };
      });
      if (group.length) col++;
    });
    nodes.forEach(n => { if (!pos[n.id]) pos[n.id] = { x: W / 2 + (Math.random() - 0.5) * 200, y: H / 2 + (Math.random() - 0.5) * 200 }; });
    setPositions(pos);
  }, [nodes]);

  const handleMouseDown = (e, nid) => {
    e.stopPropagation();
    const svg = svgRef.current.getBoundingClientRect();
    setDragging(nid);
    setOffset({ x: e.clientX - svg.left - (positions[nid]?.x || 0), y: e.clientY - svg.top - (positions[nid]?.y || 0) });
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const svg = svgRef.current.getBoundingClientRect();
    setPositions(p => ({ ...p, [dragging]: { x: Math.max(20, Math.min(W - 20, e.clientX - svg.left - offset.x)), y: Math.max(20, Math.min(H - 20, e.clientY - svg.top - offset.y)) } }));
  }, [dragging, offset]);

  const handleMouseUp = () => setDragging(null);

  const NODE_ICONS = { evidence: "E", claim: "C", obligation: "O", datapoint: "D", framework: "F", requirement: "R", supplier: "S", engagement: "G" };

  return (
    <svg ref={svgRef} width="100%" viewBox={`0 0 ${W} ${H}`} data-testid="lineage-graph"
      style={{ background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, cursor: dragging ? "grabbing" : "default" }}
      onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
      role="img" aria-label="Evidence lineage graph showing data provenance">
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="25" refY="5" markerWidth="5" markerHeight="5" orient="auto"><path d="M0,0 L10,5 L0,10 Z" fill={C.dim} /></marker>
        <filter id="glow"><feGaussianBlur stdDeviation="3" result="g" /><feMerge><feMergeNode in="g" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      {links.map((l, i) => {
        const s = positions[l.source] || { x: 0, y: 0 };
        const t = positions[l.target] || { x: 0, y: 0 };
        const mx = (s.x + t.x) / 2, my = (s.y + t.y) / 2;
        const hi = selected === l.source || selected === l.target;
        return (
          <g key={i}>
            <line x1={s.x} y1={s.y} x2={t.x} y2={t.y} stroke={hi ? C.blue + "88" : C.dim + "44"} strokeWidth={hi ? 2 : 1} markerEnd="url(#arrow)" strokeDasharray={hi ? "none" : "4 3"} />
            <text x={mx} y={my - 4} textAnchor="middle" fill={hi ? C.text : C.dim} fontSize={8} opacity={hi ? 1 : 0.5}>{l.label}</text>
          </g>
        );
      })}
      {nodes.map(n => {
        const p = positions[n.id] || { x: 0, y: 0 };
        const isSel = selected === n.id;
        const col = n.color || NODE_COLORS[n.type] || C.muted;
        const r = isSel ? 20 : 15;
        return (
          <g key={n.id} onMouseDown={e => handleMouseDown(e, n.id)} onClick={() => onSelect(n.id)} style={{ cursor: "grab" }}>
            <circle cx={p.x} cy={p.y} r={r} fill={isSel ? col + "33" : C.surface} stroke={col} strokeWidth={isSel ? 2.5 : 1.5} filter={isSel ? "url(#glow)" : undefined} />
            <text x={p.x} y={p.y + 1} textAnchor="middle" dominantBaseline="middle" fill={col} fontSize={10} fontWeight={700} pointerEvents="none">{NODE_ICONS[n.type] || "?"}</text>
            <text x={p.x} y={p.y + r + 10} textAnchor="middle" fill={isSel ? C.text : C.muted} fontSize={8} fontWeight={isSel ? 600 : 400} pointerEvents="none">{n.label?.length > 22 ? n.label.slice(0, 20) + "..." : n.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function NodeDetail({ node }) {
  if (!node) return <div style={{ padding: 16, textAlign: "center", color: C.dim, fontSize: 11 }}>Click a node to see details</div>;
  const col = node.color || NODE_COLORS[node.type] || C.muted;
  return (
    <div style={{ padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${col}33` }} data-testid="node-detail" role="region" aria-label={`Details for ${node.label}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: col }} />
        <span style={{ fontSize: 10, fontWeight: 600, color: col, textTransform: "uppercase", letterSpacing: 1 }}>{node.type}</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: C.text }}>{node.label}</div>
      <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.6 }}>
        {Object.entries(node.data || {}).map(([k, v]) => (
          <div key={k}><span style={{ color: C.dim }}>{k}:</span> <span style={{ color: C.text }}>{String(v)}</span></div>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 9, color: C.dim }}>ID: {node.id}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// NOTIFICATION PANEL
// ═══════════════════════════════════════════════════════════════

function NotificationPanel({ notifications, onMarkRead }) {
  const sevColors = { error: C.red, warning: C.amber, success: C.emerald, info: C.blue };
  return (
    <div role="region" aria-label="Notification center" data-testid="notification-panel">
      <div style={{ fontSize: 11, fontWeight: 700, color: C.cyan, letterSpacing: 0.5, marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
        NOTIFICATIONS
        <span style={{ fontSize: 9, fontWeight: 400, color: C.muted }}>{notifications.filter(n => !n.read).length} unread</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {notifications.map(n => (
          <button key={n.id} onClick={() => onMarkRead(n.id)} data-testid={`notification-${n.id}`}
            style={{ textAlign: "left", padding: "8px 10px", borderRadius: 6, background: n.read ? C.surface : C.card, border: `1px solid ${n.read ? C.border : (sevColors[n.severity] || C.border) + "44"}`, cursor: "pointer", opacity: n.read ? 0.6 : 1, transition: "all 0.2s" }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 2 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: n.read ? C.dim : (sevColors[n.severity] || C.muted), flexShrink: 0 }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: n.read ? C.muted : C.text, flex: 1 }}>{n.title}</span>
            </div>
            <div style={{ fontSize: 9, color: C.dim, marginLeft: 12 }}>{n.body}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TREND BAR
// ═══════════════════════════════════════════════════════════════

function TrendBar({ trends }) {
  if (!trends?.length) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} role="region" aria-label="Platform metric trends" data-testid="trend-bar">
      {trends.map(t => {
        const pct = t.pct_change || 0;
        const arrow = pct > 0 ? "\u2191" : pct < 0 ? "\u2193" : "\u2192";
        return (
          <div key={t.metric} style={{ padding: "6px 10px", borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, minWidth: 90, flex: "1 1 90px" }}>
            <div style={{ fontSize: 8, color: C.dim, letterSpacing: 0.5, textTransform: "uppercase" }}>{t.metric}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: t.color }}>{t.value}</span>
              <span style={{ fontSize: 11, color: t.is_positive ? C.emerald : C.red, fontWeight: 600 }}>
                {arrow} {pct > 0 ? "+" : ""}{pct}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function PlatformIntelligence() {
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("lineage");
  const [searchQuery, setSearchQuery] = useState("");
  const [lineageData, setLineageData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [trends, setTrends] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [conflicts, setConflicts] = useState(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    piFetch("/lineage/cl_01").then(d => d && setLineageData(d));
    piFetch("/notifications").then(d => d && setNotifications(d.notifications || []));
    piFetch("/trends").then(d => d && setTrends(d.trends || []));
  }, []);

  const handleMarkRead = useCallback(async (nid) => {
    await piFetch("/notifications/read", { method: "PUT", body: JSON.stringify({ notification_id: nid }) });
    setNotifications(prev => prev.map(n => n.id === nid ? { ...n, read: true } : n));
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    const d = await piFetch(`/search?q=${encodeURIComponent(searchQuery)}`);
    if (d) setSearchResults(d.results || []);
  }, [searchQuery]);

  const runConflictScan = useCallback(async () => {
    setScanning(true);
    const d = await piFetch("/ai/conflicts", { method: "POST" });
    if (d) setConflicts(d);
    setScanning(false);
  }, []);

  const selectedNode = lineageData?.nodes?.find(n => n.id === selected);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Segoe UI', system-ui, sans-serif" }} data-testid="platform-intelligence">
      {/* Header */}
      <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg + "f0", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff" }}>D</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }} data-testid="pi-title">Platform Intelligence</div>
              <div style={{ fontSize: 8, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase" }}>Phase 3 — Lineage . Trends . Notifications . Conflicts</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {["lineage", "search", "conflicts"].map(v => (
              <button key={v} onClick={() => setView(v)} data-testid={`pi-tab-${v}`}
                style={{ padding: "4px 10px", borderRadius: 5, border: `1px solid ${view === v ? C.blue : C.border}`, background: view === v ? C.blue + "18" : "transparent", color: view === v ? C.blue : C.muted, fontSize: 10, fontWeight: 600, cursor: "pointer" }}
                aria-pressed={view === v}>{v.charAt(0).toUpperCase() + v.slice(1)}</button>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 10 }}><TrendBar trends={trends} /></div>
      </div>

      {/* Main */}
      <div style={{ display: "flex", gap: 0, maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ flex: 1, padding: "16px 16px 16px 20px", minWidth: 0 }}>
          {/* Search input */}
          <div style={{ marginBottom: 10 }}>
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()} placeholder="Search nodes, claims, evidence..."
              data-testid="pi-search-input" aria-label="Search"
              style={{ width: "100%", padding: "7px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 11, outline: "none", boxSizing: "border-box" }} />
          </div>

          {view === "lineage" && lineageData && (
            <>
              <ForceGraph nodes={lineageData.nodes} links={lineageData.links} selected={selected} onSelect={setSelected} />
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }} role="legend">
                {Object.entries(NODE_COLORS).map(([type, color]) => (
                  <div key={type} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: C.muted }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
                    {type}
                  </div>
                ))}
              </div>
            </>
          )}

          {view === "search" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }} data-testid="search-results">
              {searchResults.length === 0 && <div style={{ color: C.dim, fontSize: 11, padding: 16, textAlign: "center" }}>Type a query and press Enter to search across all modules</div>}
              {searchResults.map((r, i) => (
                <div key={i} style={{ textAlign: "left", padding: "8px 12px", borderRadius: 6, background: C.card, border: `1px solid ${(NODE_COLORS[r.type] || C.border)}22` }} data-testid={`search-result-${i}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: NODE_COLORS[r.type] || C.muted }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{r.title}</span>
                    <span style={{ fontSize: 9, color: C.dim, marginLeft: "auto" }}>{r.type} . {r.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {view === "conflicts" && (
            <div style={{ padding: 16, borderRadius: 8, background: C.card, border: `1px solid ${C.amber}22` }} data-testid="conflict-view">
              <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, marginBottom: 10 }}>AI Conflict Detection</div>
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6, marginBottom: 12 }}>
                Rule-based analysis of all claims. Detects duplicates, unsupported claims, and quantitative inconsistencies.
              </div>

              {conflicts && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 8 }}>
                    {conflicts.total_claims_analyzed} claims analyzed . {conflicts.total_conflicts} issues found ({conflicts.errors} errors, {conflicts.warnings} warnings)
                  </div>
                  {conflicts.conflicts.map((c, i) => (
                    <div key={i} style={{ padding: 10, borderRadius: 6, background: C.surface, border: `1px solid ${c.severity === "error" ? C.red : C.amber}33`, marginBottom: 6 }} data-testid={`conflict-${i}`}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span style={{ color: c.severity === "error" ? C.red : C.amber }}>{c.severity === "error" ? "X" : "!"}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, color: c.severity === "error" ? C.red : C.amber }}>{c.type.replace(/_/g, " ")}</span>
                      </div>
                      <div style={{ fontSize: 10, color: C.text, lineHeight: 1.5 }}>{c.description}</div>
                      <div style={{ fontSize: 9, color: C.emerald, marginTop: 4 }}>{c.recommendation}</div>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={runConflictScan} disabled={scanning} data-testid="run-conflict-scan-btn"
                style={{ padding: "6px 14px", borderRadius: 5, border: `1px solid ${C.purple}`, background: C.purple + "18", color: C.purple, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>
                {scanning ? "Scanning..." : "Run Conflict Scan"}
              </button>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div style={{ width: 260, borderLeft: `1px solid ${C.border}`, padding: 12, flexShrink: 0, overflowY: "auto", maxHeight: "calc(100vh - 110px)" }}>
          <NodeDetail node={selectedNode} />
          <div style={{ marginTop: 16 }}>
            <NotificationPanel notifications={notifications} onMarkRead={handleMarkRead} />
          </div>
        </div>
      </div>
    </div>
  );
}
